/**
 * The first sign-in (U1.4), as pure functions: which step comes next, what
 * the form's draft is, and the profile patch a draft becomes.
 *
 * Dependency-free apart from `src/districts.ts` (itself dependency-free) and
 * the type-only `profileDefaults`, so `test/onboarding.test.ts` loads it
 * under `node --test` — the same reason `profileDefaults.ts` is split from
 * `profile.ts`. `ProfileForm.tsx` and `Onboarding.tsx` hold no decision a
 * test could not see.
 *
 * ## Two values that were nearly one
 *
 * `onboarded_at` is the answer to "has this person been through the first
 * screen?", and nothing else. The form is always in Account afterwards, so
 * a profile with `onboarded_at` set and every field empty is a person who
 * chose "Later", not a profile that is incomplete. Nothing here reads a
 * field to decide whether to ask again.
 */
import { BEZIRKE, ORTSTEILE } from '../../../src/districts.ts'
import { homePoint, secondHomeOf, type Profile, type ProfilePatch } from './profileDefaults.ts'

/** U2.4: the taste questions exist, so the second step is on. It is a
 *  switch for the whole step, not for a person — whether *this* person is
 *  asked is `needsTaste`, which also reads whether they already have a
 *  vector. */
export const TASTE_STEP = true

/**
 * Whether the taste step applies to this person: the step is on and they have
 * no active `taste_profile` row. `hasActive` is what the app's query settled
 * on — `undefined` while it is pending or failed, which asks rather than
 * guesses: a person who really has a vector is then shown their answers, filled
 * in, and can leave with Later; a person who has none is not waved past the
 * question by a slow network. Doug's row is active (U2.3), so he is never
 * asked, and the RPC refuses a non-2.0 row besides.
 */
export function needsTaste(hasActive: boolean | undefined, enabled: boolean = TASTE_STEP): boolean {
  return enabled && hasActive !== true
}

export type Step = 'profile' | 'taste' | 'done'

/**
 * Where a person goes next. `'done'` is the app. A profile with no
 * `onboarded_at` is on the profile step; a person who has given a name but
 * not been marked onboarded is on the taste step when one applies to them
 * (the profile step's Done then writes the fields and leaves `onboarded_at`
 * for the taste step to set — `finishPatch` takes the same flag).
 *
 * `taste` is "does the taste step apply to this person" — `needsTaste`'s
 * answer — defaulted to the constant so the suite can ask both ways. Because
 * the name is written to the cached profile on the profile step's Done, this
 * is also how the screen moves on: no local step state, and a person who
 * returns with a name and no vector lands on the taste step directly.
 */
export function nextStep(
  p: Pick<Profile, 'onboarded_at' | 'display_name'>,
  taste: boolean = TASTE_STEP,
): Step {
  if (p.onboarded_at) return 'done'
  if (taste && (p.display_name ?? '').trim()) return 'taste'
  return 'profile'
}

/**
 * Whether the gate in `App.tsx` should draw the onboarding screen: the
 * profile query has *settled with a real row* that has not been through it.
 *
 * - `status !== 'success'` (pending, or errored): no. A cold load, a slow
 *   network and a failed read all draw the app, never the form — the gate
 *   must not guess a person is new from the absence of an answer.
 * - `fetching`: a cached row is on screen while the server's is on its way.
 *   A row cached before the person finished onboarding on another device
 *   still says `null`; wait for the server.
 * - `user_id === ''`: `DEFAULT_PROFILE`, which is what `mergeProfile` hands
 *   back for a user the server has no row for. A form that wrote to a row
 *   that does not exist would write nothing.
 */
export function shouldOnboard(
  status: 'pending' | 'error' | 'success',
  fetching: boolean,
  p: Pick<Profile, 'user_id' | 'onboarded_at'>,
): boolean {
  return status === 'success' && !fetching && p.user_id !== '' && !p.onboarded_at
}

// ---------------------------------------------------------------------------
// Districts
// ---------------------------------------------------------------------------

export interface BezirkGroup { bezirk: string; ortsteile: string[] }

/**
 * The 12 Bezirke with their Ortsteile, for the picker. Read off the same
 * `ORTSTEILE` / `BEZIRKE` that Places' chip row and Postgres' `districts`
 * table are built from (`districts.test.ts` holds the three together) rather
 * than querying the table: the picker then works offline, on a cold load and
 * before the table has been read, and there is no second list to drift.
 * Bezirk order is `BEZIRKE`'s; Ortsteile are alphabetical in German order.
 */
export function districtGroups(): BezirkGroup[] {
  return BEZIRKE.map(bezirk => ({
    bezirk,
    ortsteile: Object.keys(ORTSTEILE).filter(o => ORTSTEILE[o] === bezirk).sort((a, b) => a.localeCompare(b, 'de')),
  }))
}

/** Add `v` to `list`, or take it out if it is there. A new array, in the
 *  order the person tapped. */
export function toggled(list: readonly string[], v: string): string[] {
  return list.includes(v) ? list.filter(x => x !== v) : [...list, v]
}

/** The Ortsteile of a Bezirk that `chosen` holds — a Bezirk chip's count. */
export function chosenIn(group: BezirkGroup, chosen: readonly string[]): number {
  return group.ortsteile.filter(o => chosen.includes(o)).length
}

// ---------------------------------------------------------------------------
// Lines and airports
// ---------------------------------------------------------------------------

/** The lines a person can say they ride: the nine U-Bahn lines, the S-Bahn
 *  ring and its radials. The strings are what the city-news rows carry in
 *  `lines` (`today.ts`' `onMyLine` compares them exactly). */
export const U_LINES = ['U1', 'U2', 'U3', 'U4', 'U5', 'U6', 'U7', 'U8', 'U9'] as const
export const S_LINES = ['S41', 'S42', 'S1', 'S2', 'S3', 'S5', 'S7', 'S8', 'S9', 'S25', 'S26', 'S46', 'S47', 'S75'] as const

/** One airport code as typed: three letters, upper-cased, or null. */
export function normaliseAirport(raw: string): string | null {
  const s = raw.trim().toUpperCase()
  return /^[A-Z]{3}$/.test(s) ? s : null
}

/** Everything typeable into the airport box — "NOC, dub  ORK" — as the codes
 *  it holds, valid ones only, in order, without repeats; and the pieces that
 *  were not codes, so the form can say so instead of dropping them. */
export function parseAirports(raw: string): { codes: string[]; rejected: string[] } {
  const codes: string[] = []
  const rejected: string[] = []
  for (const piece of raw.split(/[\s,;]+/).filter(Boolean)) {
    const c = normaliseAirport(piece)
    if (!c) rejected.push(piece)
    else if (!codes.includes(c)) codes.push(c)
  }
  return { codes, rejected }
}

/** The second home's country picker. ISO alpha-2, as `profiles.second_home`
 *  stores it and `holidays.ts` keys its tables by; the names are for the
 *  person. A country without a holiday table is still a valid second home —
 *  `hasHolidays` is what the form asks to say so. */
export const COUNTRIES: readonly { code: string; name: string }[] = [
  { code: 'IE', name: 'Ireland' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'PT', name: 'Portugal' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'PL', name: 'Poland' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'DE', name: 'Germany' },
  { code: 'US', name: 'United States' },
]

export const countryName = (code: string): string | null =>
  COUNTRIES.find(c => c.code === code.trim().toUpperCase())?.name ?? null

// ---------------------------------------------------------------------------
// The draft
// ---------------------------------------------------------------------------

/** What the form holds while it is open. Strings and arrays, nothing derived:
 *  every field is the thing a control shows. */
export interface Draft {
  name: string
  districts: string[]
  lat: number | null
  lng: number | null
  englishOnly: boolean
  lines: string[]
  second: boolean
  secondLabel: string
  secondCountry: string
  airports: string[]
}

/** The draft a profile starts as. `secondHomeOf` is the defensive read — the
 *  column is jsonb and younger than a phone's cache. */
export function draftFrom(p: Profile): Draft {
  const home = homePoint(p)
  const sh = secondHomeOf(p)
  return {
    name: p.display_name ?? '',
    districts: [...(p.home_districts ?? [])],
    lat: home?.lat ?? null,
    lng: home?.lng ?? null,
    englishOnly: p.english_only ?? true,
    lines: [...(p.transit_lines ?? [])],
    second: !!sh,
    secondLabel: sh?.label ?? '',
    secondCountry: sh?.country ?? '',
    airports: [...(sh?.airports ?? [])],
  }
}

/** Coordinates to five places (about a metre): enough for Near me, and a pin
 *  from a map click does not carry fourteen digits of false precision. */
const round5 = (n: number) => Math.round(n * 1e5) / 1e5

/**
 * The patch a draft becomes. Every field the form owns is written, so the
 * profile after Save is the form as it was left (a cleared pin is `null`, not
 * "unchanged"). `onboarded_at` is not here: `finishPatch` adds it.
 *
 * - Name: trimmed; blank is `null`, which `display_name` allows.
 * - A pin is both halves or neither (`homePoint`'s rule).
 * - Second home: off is `null`. On with neither a label nor a country is
 *   nothing to store, so also `null` — a half-filled switch is not a second
 *   home. A label with no country keeps the label (stays and flights use
 *   it) and an empty `country`; a country alone is labelled with its name.
 */
export function patchFrom(d: Draft): ProfilePatch {
  const label = d.secondLabel.trim()
  const country = d.secondCountry.trim().toUpperCase()
  const second = d.second && (label || country)
    ? { label: label || countryName(country) || country, country, airports: [...d.airports] }
    : null
  const pin = d.lat != null && d.lng != null
  return {
    display_name: d.name.trim() || null,
    home_districts: [...d.districts],
    home_lat: pin ? round5(d.lat as number) : null,
    home_lng: pin ? round5(d.lng as number) : null,
    english_only: d.englishOnly,
    transit_lines: [...d.lines],
    second_home: second,
  }
}

/**
 * What Done and Later write: the draft's patch plus `onboarded_at`.
 *
 * - **Later** is "skip", so it never writes a blank name over nothing: an
 *   empty name is left out of the patch rather than set to `null`, and the
 *   other fields are written as the person left them.
 * - When the taste step follows (`TASTE_STEP`), Done on the profile step
 *   leaves `onboarded_at` alone — the taste step sets it.
 */
export function finishPatch(
  d: Draft,
  how: 'done' | 'later',
  now: string,
  taste: boolean = TASTE_STEP,
): ProfilePatch {
  const patch = patchFrom(d)
  if (how === 'later' && patch.display_name === null) delete patch.display_name
  if (how === 'done' && taste) return patch
  return { ...patch, onboarded_at: now }
}

/** Has the person changed anything since `base`? Compared on the patch, so
 *  "retyped the same name" and a trailing space are not changes. */
export function isChanged(d: Draft, base: Profile): boolean {
  return JSON.stringify(patchFrom(d)) !== JSON.stringify(patchFrom(draftFrom(base)))
}
